import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../contexts/ThemeContext";
import {
  X, DollarSign, Tag, FileText, Calendar,
  ArrowUpRight, ArrowDownRight, Briefcase, Wallet,
  User, Search, Building2, CheckCircle2
} from "lucide-react";
import { organizationsAPI } from "../../services/api";

const InputWrapper = ({ label, icon: Icon, children }) => (
  <div className="space-y-1.5">
     <div className="flex items-center gap-2 px-1">
        <Icon className="w-3 h-3 text-zinc-400" />
        <label className="text-[9px] font-black uppercase tracking-widest text-zinc-500 dark:text-zinc-400">
           {label}
        </label>
     </div>
     {children}
  </div>
);

const TransactionModal = ({
  isOpen,
  onClose,
  onSubmit,
  transaction,
  title,
  trainers = [],
  // Org context: when the page is filtered to a specific org we lock the
  // selector to that org; when in "all orgs" mode we let the user pick one.
  currentOrgId = "",
  organizations = [],
}) => {
  const { t } = useTranslation();
  const { colors, theme } = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    type: "income",
    category: "",
    amount: "",
    description: "",
    trainerId: "",
    organizationId: "", // Added to ensure correct gym attribution
    transactionDate: new Date().toISOString().split("T")[0],
  });

  const isSalary = title?.toLowerCase().includes('maaş') || title?.toLowerCase().includes('salary');
  const isOutcome = formData.type === 'outcome' || isSalary;
  const isAllOrgsMode = !currentOrgId || currentOrgId === 'all';
  const orgOptions = (organizations || []).filter((o) => o.id && o.id !== 'all');
  // Lazy fallback: if the parent didn't pass a usable organizations list,
  // fetch one ourselves the first time we need a name. Cached per modal mount.
  const [fetchedOrgs, setFetchedOrgs] = useState([]);

  useEffect(() => {
    // When editing an existing transaction always preserve its org. When
    // creating a new one default to the page's currentOrgId (so a manager or
    // a super-admin filtered to a single gym never has to pick again, and a
    // super-admin in 'all' mode starts empty and must pick).
    const defaultOrg = transaction?.organizationId
      || (currentOrgId && currentOrgId !== 'all' ? currentOrgId : "");

    if (transaction) {
      setFormData({
        type: transaction.type || "income",
        category: transaction.category || "",
        amount: transaction.amount || "",
        description: transaction.description || "",
        trainerId: transaction.trainerId || "",
        organizationId: defaultOrg,
        transactionDate: transaction.transactionDate
          ? transaction.transactionDate.split("T")[0]
          : new Date().toISOString().split("T")[0],
      });
    } else {
      setFormData({
        type: isSalary ? "outcome" : "income",
        category: isSalary ? "Maaş Ödemesi" : "",
        amount: "",
        description: "",
        trainerId: "",
        organizationId: defaultOrg,
        transactionDate: new Date().toISOString().split("T")[0],
      });
    }
    setSearchTerm("");
  }, [transaction, isOpen, isSalary, currentOrgId]);

  const filteredTrainers = useMemo(() => {
    // When the page is locked to a specific org, only show that org's
    // trainers — picking a trainer from another org would change formData's
    // organizationId out from under us.
    const scoped = isAllOrgsMode
      ? trainers
      : trainers.filter((tr) => tr.organizationId === currentOrgId);
    if (!searchTerm) return scoped;
    const lowerSearch = searchTerm.toLowerCase();
    return scoped.filter(t =>
      (t.name && t.name.toLowerCase().includes(lowerSearch)) ||
      (t.organizationName && t.organizationName.toLowerCase().includes(lowerSearch))
    );
  }, [trainers, searchTerm, isAllOrgsMode, currentOrgId]);

  const selectedOrgName = useMemo(() => {
    const oid = (formData.organizationId || "").trim();
    if (!oid) return "";
    const norm = (v) => (v || "").trim().toLowerCase();
    const target = norm(oid);
    // Try several sources in order: the parent's organizations prop, our own
    // fetched list (lazy fallback), and finally the trainer enrichment.
    const fromOrgs = orgOptions.find((o) => norm(o.id) === target)?.name;
    if (fromOrgs) return fromOrgs;
    const fromFetched = fetchedOrgs.find((o) => norm(o.id) === target)?.name;
    if (fromFetched) return fromFetched;
    const fromTrainers = trainers.find((tr) => norm(tr.organizationId) === target)?.organizationName;
    if (fromTrainers) return fromTrainers;
    return oid;
  }, [formData.organizationId, orgOptions, trainers, fetchedOrgs]);

  // If the modal is open and we still can't resolve a name from any cached
  // source, hit the API once. This protects against AppContext racing the
  // modal open or the parent forgetting to pass `organizations`.
  useEffect(() => {
    if (!isOpen) return undefined;
    const oid = (formData.organizationId || "").trim();
    if (!oid || oid === 'all') return undefined;
    const haveLocal =
      orgOptions.some((o) => (o.id || "").trim().toLowerCase() === oid.toLowerCase()) ||
      fetchedOrgs.some((o) => (o.id || "").trim().toLowerCase() === oid.toLowerCase()) ||
      trainers.some((tr) => (tr.organizationId || "").trim().toLowerCase() === oid.toLowerCase() && tr.organizationName);
    if (haveLocal) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await organizationsAPI.getAll();
        const list = res?.data?.data || res?.data?.items || res?.data || [];
        if (!cancelled && Array.isArray(list)) setFetchedOrgs(list);
      } catch (_) { /* ignore — we'll keep showing the id */ }
    })();
    return () => { cancelled = true; };
  }, [isOpen, formData.organizationId, orgOptions, fetchedOrgs, trainers]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /**
   * Selection needs to be unique. 
   * Since a trainer can exist in multiple organizations with the same ID,
   * we use a composite selection strategy.
   */
  const handleSelectTrainer = (trainerId, organizationId) => {
    setFormData(prev => ({ 
      ...prev, 
      trainerId: trainerId,
      organizationId: organizationId 
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isSalary && !formData.trainerId) {
       alert("Lütfen maaş ödenecek eğitmeni seçin.");
       return;
    }
    if (!formData.organizationId) {
       alert("Lütfen kurum (organizasyon) seçin.");
       return;
    }
    onSubmit(formData);
  };

  if (!isOpen) return null;

  // Composite key for selection check
  const selectedKey = formData.trainerId && formData.organizationId 
    ? `${formData.trainerId}-${formData.organizationId}` 
    : "";

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/40 backdrop-blur-md animate-in fade-in duration-300">
      <div
        className={`
          w-full max-w-lg bg-white dark:bg-zinc-950
          border border-zinc-200 dark:border-white/10
          rounded-[2rem] shadow-2xl overflow-hidden
          max-h-[92vh] flex flex-col
          animate-in zoom-in-95 slide-in-from-bottom-4 duration-500
        `}
      >
        <div
          className={`
            flex items-center justify-between gap-3 p-4 px-5 sm:p-6 sm:px-8 border-b border-zinc-100 dark:border-white/5 shrink-0
            ${isSalary ? 'bg-blue-500/5' : isOutcome ? 'bg-rose-500/5' : 'bg-emerald-500/5'}
          `}
        >
          <div className="flex items-center gap-3">
             <div className={`
               w-10 h-10 rounded-2xl flex items-center justify-center
               ${isSalary ? 'bg-blue-500' : isOutcome ? 'bg-rose-500' : 'bg-emerald-500'}
             `}>
                {isSalary ? <Wallet className="w-5 h-5 text-white" /> : isOutcome ? <ArrowDownRight className="w-5 h-5 text-white" /> : <ArrowUpRight className="w-5 h-5 text-white" />}
             </div>
             <div>
               <h2 className="text-lg font-black tracking-tight text-zinc-900 dark:text-zinc-100">
                 {title}
               </h2>
               <p className="text-[10px] uppercase font-bold tracking-widest text-zinc-400">
                 {isSalary ? 'Personel Gider Kaydı' : 'Finansal Hareket Girişi'}
               </p>
             </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl bg-zinc-100 dark:bg-white/5 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="p-5 sm:p-8 space-y-5 overflow-y-auto custom-scrollbar min-h-0 flex-1"
        >
           {isSalary && (
             <div className="space-y-3">
                <InputWrapper label="İlgili Eğitmen" icon={User}>
                  <div className="relative group">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 group-focus-within:text-blue-500 transition-colors" />
                    <input
                      type="text"
                      placeholder="Eğitmen veya Kurum Ara..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className={`
                        w-full h-11 pl-11 pr-4 bg-zinc-50 dark:bg-white/5 border border-zinc-200 dark:border-white/10 rounded-2xl
                        text-xs font-bold outline-none focus:border-blue-500 transition-all
                      `}
                    />
                  </div>
                </InputWrapper>

                {/* SEARCHABLE LIST AREA */}
                <div className={`
                  max-h-[160px] overflow-y-auto pr-1 space-y-1 custom-scrollbar
                  bg-zinc-50 dark:bg-white/5 rounded-2xl p-2 border border-zinc-100 dark:border-white/5
                `}>
                  {filteredTrainers.length > 0 ? (
                    filteredTrainers.map(t => {
                      const currentKey = `${t.id}-${t.organizationId}`;
                      const isSelected = selectedKey === currentKey;
                      return (
                        <div
                          key={currentKey}
                          onClick={() => handleSelectTrainer(t.id, t.organizationId)}
                          className={`
                            w-full flex items-center justify-between p-3 rounded-xl transition-all cursor-pointer select-none
                            ${isSelected 
                              ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20' 
                              : 'hover:bg-zinc-200 dark:hover:bg-white/10 text-zinc-700 dark:text-zinc-300'}
                          `}
                        >
                          <div className="flex flex-col items-start min-w-0">
                            <span className="text-[11px] font-black uppercase tracking-tight truncate w-full">
                              {t.name}
                            </span>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <Building2 className={`w-2.5 h-2.5 ${isSelected ? 'text-blue-100' : 'text-zinc-400'}`} />
                              <span className={`text-[9px] font-bold uppercase truncate ${isSelected ? 'text-blue-100' : 'text-zinc-400'}`}>
                                {t.organizationName}
                              </span>
                            </div>
                          </div>
                          {isSelected && <CheckCircle2 className="w-4 h-4 text-white" />}
                        </div>
                      );
                    })
                  ) : (
                    <div className="py-8 text-center text-[10px] font-black text-zinc-400 uppercase tracking-widest">
                      Eğitmen Bulunamadı
                    </div>
                  )}
                </div>
             </div>
           )}

           {/* Organization picker for non-salary flows.
               - currentOrgId is a real org → show locked, read-only badge.
               - currentOrgId is 'all' / empty → show a required dropdown so
                 super-admins must pick the target gym before submitting. */}
           {!isSalary && (
             <InputWrapper label="Kurum" icon={Building2}>
                {!isAllOrgsMode ? (
                  <div className={`
                    w-full h-11 px-4 flex items-center gap-2
                    bg-zinc-100 dark:bg-white/5 border border-zinc-200 dark:border-white/10
                    rounded-2xl text-xs font-black uppercase tracking-tight text-zinc-700 dark:text-zinc-200
                    cursor-not-allowed
                  `}>
                    <Building2 className="w-3.5 h-3.5 text-zinc-400" />
                    <span className="truncate">{selectedOrgName || '—'}</span>
                    <span className="ml-auto text-[9px] font-bold text-zinc-400">SEÇİLİ KURUM</span>
                  </div>
                ) : (
                  <select
                    name="organizationId"
                    value={formData.organizationId}
                    onChange={handleChange}
                    required
                    className={`
                      w-full h-11 px-4 bg-zinc-50 dark:bg-white/5 border border-zinc-200 dark:border-white/10 rounded-2xl
                      text-xs font-black uppercase outline-none focus:border-blue-500 transition-all
                    `}
                  >
                    <option value="">Kurum Seçin...</option>
                    {(orgOptions.length > 0 ? orgOptions : fetchedOrgs.filter((o) => o.id && o.id !== 'all')).map((o) => (
                      <option key={o.id} value={o.id}>{o.name}</option>
                    ))}
                  </select>
                )}
             </InputWrapper>
           )}

           <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <InputWrapper label="İşlem Tipi" icon={ArrowUpRight}>
                 <select
                    name="type"
                    disabled={isSalary}
                    value={formData.type}
                    onChange={handleChange}
                    className={`
                      w-full h-11 px-4 bg-zinc-50 dark:bg-white/5 border border-zinc-200 dark:border-white/10 rounded-2xl
                      text-xs font-black uppercase outline-none focus:border-blue-500 transition-all
                    `}
                 >
                    <option value="income">Gelir</option>
                    <option value="outcome">Gider</option>
                 </select>
              </InputWrapper>

              <InputWrapper label="Kategori" icon={Tag}>
                 <input
                    type="text"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    required
                    readOnly={isSalary}
                    className={`
                      w-full h-11 px-4 bg-zinc-50 dark:bg-white/5 border border-zinc-200 dark:border-white/10 rounded-2xl
                      text-xs font-bold outline-none focus:border-blue-500 transition-all
                      ${isSalary ? 'opacity-60 grayscale' : ''}
                    `}
                    placeholder="Kategori girin..."
                 />
              </InputWrapper>
           </div>

           <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <InputWrapper label="Tutar (TRY)" icon={DollarSign}>
                 <div className="relative">
                    <input
                        type="number"
                        name="amount"
                        value={formData.amount}
                        onChange={handleChange}
                        required
                        min="0"
                        step="0.01"
                        className={`
                          w-full h-11 pl-4 pr-10 bg-zinc-50 dark:bg-white/5 border border-zinc-200 dark:border-white/10 rounded-2xl
                          text-sm font-black outline-none focus:border-blue-500 transition-all
                        `}
                        placeholder="0.00"
                    />
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] font-black text-zinc-400">₺</div>
                 </div>
              </InputWrapper>

              <InputWrapper label="İşlem Tarihi" icon={Calendar}>
                 <input
                    type="date"
                    name="transactionDate"
                    value={formData.transactionDate}
                    onChange={handleChange}
                    required
                    className={`
                      w-full h-11 px-4 bg-zinc-50 dark:bg-white/5 border border-zinc-200 dark:border-white/10 rounded-2xl
                      text-xs font-bold outline-none focus:border-blue-500 transition-all
                    `}
                 />
              </InputWrapper>
           </div>

           <InputWrapper label="Açıklama" icon={FileText}>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="3"
                className={`
                  w-full p-4 bg-zinc-50 dark:bg-white/5 border border-zinc-200 dark:border-white/10 rounded-2xl
                  text-xs font-medium outline-none focus:border-blue-500 transition-all resize-none
                `}
                placeholder="İşlem ile ilgili detayları girin..."
              ></textarea>
           </InputWrapper>

           <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 h-12 bg-zinc-100 dark:bg-white/5 text-zinc-500 dark:text-zinc-400 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-zinc-200 dark:hover:bg-white/10 transition-all"
              >
                İptal
              </button>
              <button
                type="submit"
                className={`
                  flex-[2] h-12 rounded-2xl text-[10px] font-black uppercase tracking-widest text-white
                  shadow-lg transition-all transform hover:scale-[1.02] active:scale-95
                  ${isSalary ? 'bg-blue-600 shadow-blue-500/20' : isOutcome ? 'bg-rose-600 shadow-rose-500/20' : 'bg-emerald-600 shadow-emerald-500/20'}
                `}
              >
                {transaction ? "İşlemi Güncelle" : "İşlemi Oluştur"}
              </button>
           </div>
        </form>
      </div>
    </div>
  );
};

export default TransactionModal;
